import React, { FunctionComponent, useState } from "react";
import { Table } from "react-bootstrap";
import { toast } from "react-toastify";
import dayjs from "dayjs";

import ExportAsCsvButton from "../registration/ExportAsCsvButton";
import { SearchIcon, TransferIcon, TrashIcon } from "../common/sidebar/icons";
import { joyColorClass } from "../common/joyColor";
import { RegistrationDetails } from "../registration/RegistrationDetails";
import { useFormSchema } from "../../hooks/useFormSchema";
import { useWaitlist } from "../../hooks/useWaitlist";
import { useSchemaProperties } from "../../hooks/useSchemaProperties";
import { useLimits } from "../../hooks/useLimits";
import FirebaseService from "../../actions/firebase/FirebaseActions";
import { useAuthentication } from "../../hooks/useAuthentication";
import useSubscriptionType from "../../hooks/useSubscriptionType";
import { ContentLayout } from "../common/Layout";
import { getExportRegistrations, getFormattedRegistrations } from "../../helper/getFormattedRegistrations";
import { useUserIdParam } from "../../hooks/useUserIdParam";
import "../registration/Registrations.scss";

interface Props {
  isAuthenticated: boolean;
}

const Waitlist: FunctionComponent<Props> = ({ isAuthenticated }) => {
  const [buttonsDisabled, disableButtons] = useState<boolean>(false);
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const userId = useUserIdParam();
  useAuthentication(isAuthenticated);

  const [entries, entriesLoading, entriesError] = useWaitlist(userId);
  const [limits, limitsLoading, limitsError] = useLimits(userId);
  const [schemaProperties, schemaPropertiesLoading, schemaPropertiesError] = useSchemaProperties(userId);
  const [formSchema] = useFormSchema(userId);
  const subscriptionType = useSubscriptionType(userId);

  const isLoading = entriesLoading || schemaPropertiesLoading || limitsLoading;
  const isError = entriesError || schemaPropertiesError || limitsError;

  if (isError) return <p>Fehler beim Laden der Daten</p>;

  const transferEntry = (entryId: string): void => {
    if (!window.confirm("Eintrag in die Anmeldungen übernehmen? Die gewählten Angebote werden dabei belegt.")) {
      return;
    }
    disableButtons(true);
    FirebaseService.transferWaitlistEntry(userId, entryId)
      .then(() => {
        toast.success("Der Eintrag wurde in die Anmeldungen übernommen.");
        setSelectedId((current) => (current === entryId ? null : current));
      })
      .catch(() => toast.error("Der Eintrag konnte nicht übernommen werden. Bitte versuchen Sie es später nochmals."))
      .finally(() => disableButtons(false));
  };

  const deleteEntry = (entryId: string): void => {
    if (!window.confirm("Eintrag wirklich von der Warteliste löschen?")) {
      return;
    }
    disableButtons(true);
    FirebaseService.deleteWaitlistEntry(userId, entryId)
      .then(() => setSelectedId((current) => (current === entryId ? null : current)))
      .catch(() => toast.error("Der Eintrag konnte nicht gelöscht werden. Bitte versuchen Sie es später nochmals."))
      .finally(() => disableButtons(false));
  };

  const saveEntry = async (entryId: string, formData: Record<string, unknown>): Promise<void> => {
    try {
      await FirebaseService.updateWaitlistEntry(userId, entryId, formData);
      toast.success("Der Eintrag wurde gespeichert.");
    } catch {
      toast.error("Der Eintrag konnte nicht gespeichert werden. Bitte versuchen Sie es später nochmals.");
    }
  };

  const limitProperties = limits?.map((limit) => limit.property);
  const formattedEntries = getFormattedRegistrations(entries);
  const entriesForExport = getExportRegistrations(formattedEntries, limits);
  const visibleProperties = schemaProperties.slice(0, 6);

  const term = search.trim().toLowerCase();
  const visibleEntries = term
    ? formattedEntries.filter((entry) =>
        Object.values(entry.formData).some((value) =>
          String(value ?? "")
            .toLowerCase()
            .includes(term)
        )
      )
    : formattedEntries;

  const selectedEntry = entries.find((entry) => entry.id === selectedId);

  return (
    <ContentLayout
      isLoading={isLoading}
      title={"Warteliste"}
      subtitle={`${entries.length} Einträge`}
      siteTitle={"Spielgruppe Online - Warteliste"}
      actions={
        <>
          <label className="header-search">
            <SearchIcon style={{ color: "var(--text-faint)" }} />
            <input
              value={search}
              placeholder="Suchen …"
              aria-label="Warteliste durchsuchen"
              onChange={(event) => setSearch(event.target.value)}
            />
          </label>
          <ExportAsCsvButton
            properties={schemaProperties}
            registrations={entriesForExport}
            limits={limits}
            subscriptionType={subscriptionType}
          />
        </>
      }>
      <div className={`split-view${selectedEntry ? " has-detail" : ""}`}>
        <section className="panel">
          <div className="registrations-table-wrapper">
            <Table hover className={"registrations-table sticky-table"}>
              <thead>
                <tr>
                  {visibleProperties.map((property) => (
                    <th key={property}>{property}</th>
                  ))}
                  <th>Wunschtage</th>
                  <th>Zeitpunkt</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {visibleEntries.map((entry) => (
                  <tr
                    key={entry.id}
                    className={entry.id === selectedId ? "is-selected" : undefined}
                    onClick={() => setSelectedId(entry.id)}>
                    {visibleProperties.map((property) => (
                      <td key={property}>{entry.formData[property]}</td>
                    ))}
                    <td>
                      {limitProperties
                        ?.filter((property) => entry.formData[property])
                        .map((property) => (
                          <span key={property} className={`tag-pill tag-pill${joyColorClass(property)} me-1`}>
                            {property}
                          </span>
                        ))}
                    </td>
                    <td>{dayjs(entry.creation.createdAt).format("DD.MM.YYYY HH:mm")}</td>
                    <td>
                      <div className="data-row__actions">
                        <button
                          type="button"
                          title="In Anmeldungen übernehmen"
                          className="btn btn-icon"
                          disabled={buttonsDisabled}
                          onClick={(event) => {
                            event.stopPropagation();
                            transferEntry(entry.id);
                          }}>
                          <TransferIcon />
                        </button>
                        <button
                          type="button"
                          title="Löschen"
                          className="btn btn-icon btn-icon-danger"
                          disabled={buttonsDisabled}
                          onClick={(event) => {
                            event.stopPropagation();
                            deleteEntry(entry.id);
                          }}>
                          <TrashIcon />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
          <div className="panel__bar">
            <span>
              {visibleEntries.length} von {formattedEntries.length} Einträgen · Übernehmen belegt die Wunschtage als
              Anmeldung
            </span>
          </div>
        </section>

        {selectedEntry && (
          <RegistrationDetails
            registration={selectedEntry}
            properties={schemaProperties}
            limits={limits}
            formSchema={formSchema}
            title={"Wartelisteneintrag"}
            offersLabel={"Wunschtage"}
            onClose={() => setSelectedId(null)}
            onDelete={() => deleteEntry(selectedEntry.id)}
            onSave={(formData) => saveEntry(selectedEntry.id, formData)}
            onTransfer={() => transferEntry(selectedEntry.id)}
          />
        )}
      </div>
    </ContentLayout>
  );
};

export default Waitlist;
